import { RouterRootState } from 'connected-react-router';
import { Field, useFormikContext } from 'formik';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { DEF_PG_SIZE_DROP_DOWN_MIN, DEF_PG_SIZE_FILTER } from '../../constants/maximum-page-size';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { useLocale } from '../../contexts/localeContext';
import { getBiometricNamesWithCurrentSelectedDevice } from '../../services/biometricService';
import { getLocationTypeNames } from '../../services/locationTypeService';
import { getModeTypeNames } from '../../services/modeTypeService';
import { getThreatLevelNames } from '../../services/threatLevelService';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import Togglebox from '../togglebox/togglebox';
import { getListQueryForModeIds } from '../../utilities/modeTypeUtils';
import { ListQuery } from '../../models/ListQuery';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

export const DeviceDetails: FC = () => {
    const { translate } = useLocale();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const { setFieldValue, values } = useFormikContext<any>();

    return <div>
        <div className="flex flex-row">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='type'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.type)}:</Label>
                <Field
                    name="type"
                    type="text"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>

            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='site'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.site)}:</Label>
                <Field
                    name="site"
                    type="text"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
        </div>

        <div className="flex flex-row flex-1 pt-3 ">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='defaultModeId'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.defaultMode)}:</Label>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.defaultMode)}
                    key={"defaultModeId"}
                    accessor='defaultModeId'
                    pageSize={DEF_PG_SIZE_DROP_DOWN_MIN}
                    dataMethod={(listParameters?: ListQuery) => getModeTypeNames(getListQueryForModeIds(values.profileSysname, listParameters))}
                    searchAccessor={'trnName'}
                    onSelect={(_,item) => {
                        setFieldValue('defaultMode', item?.label)
                        setFieldValue('defaultModeId', item?.value)
                    }}
                    selectedItemLabel={values.defaultMode} />
            </div>

            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='functionId'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.function)}:</Label>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.function)}
                    key={"functionId"}
                    accessor='functionId'
                    dataMethod={getLocationTypeNames}
                    searchAccessor={'ltName'}
                    onSelect={(_,item) => {
                        setFieldValue('function', item?.label)
                        setFieldValue('functionId', item?.value)
                    }}
                    selectedItemLabel={values.function} />
            </div>
        </div>

        <div className="flex flex-row flex-1 pt-3">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='biometricInput'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.biometricInput)}:</Label>
                <div className="flex space-x-1">
                    <div className="w-full" id="biometricInputSelectFieldId">
                        <PaginatedSearchableSelectField
                            name={translate.byKey(TranslationKeys.biometricInput)}
                            key={"biometricInput"}
                            accessor='biometricInput'
                            dataMethod={listParameters => getBiometricNamesWithCurrentSelectedDevice(listParameters, parseInt(pageId))}
                            searchAccessor={'baName'}
                            onSelect={(_,item) => {
                                setFieldValue('biometricInputLabel', item?.label)
                                setFieldValue('biometricInput', item?.value)
                            }}
                            onDelete={(event) => setFieldValue('biometricInput', undefined)}
                            selectedItemLabel={values.biometricInputLabel}
                            pageSize={DEF_PG_SIZE_DROP_DOWN_MIN} />
                    </div>
                </div>
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='threatLevelId'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.threatLevel)}:</Label>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.threatLevel)}
                    key={"threatLevelId"}
                    accessor='threatLevelId'
                    dataMethod={getThreatLevelNames}
                    searchAccessor={'tlName'}
                    onSelect={(_,item) => {
                        setFieldValue('threatLevel', item?.label)
                        setFieldValue('threatLevelId', item?.value)
                    }}
                    selectedItemLabel={values.threatLevel} />
            </div>
        </div>

        <div className="flex flex-row flex-1 pt-3">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='position'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.position)}:</Label>
                <Field
                    name="position"
                    type="text"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='version'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.version)}:</Label>
                <Field
                    name="version"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
        </div>

        <div className="flex flex-row flex-1 pt-3">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='devLicense'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.licensed)}:</Label>
                <Field
                    name="devLicense"
                    type="text"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
            <div className="flex flex-col flex-1 pr-3">
            <Label
                    htmlFor="musteringPoint"
                    className="text-xs text-gray-600 mt-1 my-1">{translate.byKey(TranslationKeys.musteringPoint)}:</Label>
                <div className="my-0">
                    <Togglebox name="musteringPoint" label="" />
                </div>
            </div>
        </div>
    </div>
};
